import { Check, ChevronRight, CircleCheckBig, Clock, OctagonAlert, RefreshCw } from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTasks } from '../../../lib/tasks';
import { bucketTasks } from '../../../lib/agent-task-buckets';
import { useNewSince } from '../../../lib/use-last-visited';
import { countNewSince } from '../../../lib/summary-freshness';
import { Badge, Card, IconCountBadge, IconCountBadges, Typography } from '../ui';
import { AgentEntryRow } from '../agents/AgentEntryRow';
import styles from './tasks.module.css';

// The compact tasks summary on Your Agents: just the count per status category and a link into the full
// Tasks list — NOT the list itself. Figma `15378:117541` → the "Space tasks" tile, 249×72: the check
// glyph 4 from the plain "Tasks" heading (Heading 04, 14px), a row of 12px ICON-COUNT badges 8 under it
// (Waiting / In progress / Done, plus Failed only when something failed), and the drill-in chevron
// beside that whole body, centred against the card — like the Files/Apps tiles next to it. The whole
// card opens `/tasks`. With no tasks it keeps its place beside them and states the empty case
// ("Tasks" / "No tasks yet") rather than vanishing.
//
// **The heading is the bare word, and the counts live in the badges.** It used to read "{count} Tasks
// your agents are working on", which said the total twice over — once in prose and once as the sum of
// the chips under it — and left no room on a 249px tile for the badges that carry the breakdown.
//
// The heading line also carries a "N new" badge — tasks created since the reader last opened `/tasks`
// on this device (`lib/last-visited.ts` + `lib/summary-freshness.ts`), the same OUTLINED badge the
// Routines, Files and Apps tiles beside it wear. It costs nothing extra: the list is already in hand.

// `icon` is the 12px glyph every badge leads with (Figma 15378:117541 — "Use 12px icons for badges"),
// shared 1:1 with native's status icons; `labelKey` is the sentence it replaces, announced on the
// badge's `aria-label`.
const CATS = [
  { key: 'waiting' as const, labelKey: 'agents.taskChip.waiting', variant: 'waiting-on-you' as const, icon: Clock },
  { key: 'inProgress' as const, labelKey: 'agents.taskChip.inProgress', variant: 'in-progress' as const, icon: RefreshCw },
  { key: 'done' as const, labelKey: 'agents.taskChip.done', variant: 'done' as const, icon: Check },
  { key: 'failed' as const, labelKey: 'agents.taskChip.failed', variant: 'failed' as const, icon: OctagonAlert },
] satisfies { key: string; labelKey: string; variant: string; icon: ComponentType<{ size?: number }> }[];

// `emptyLabelKey` lets a caller override the heading. The narrow Your Agents screen passes
// `agents.tasksOngoing` ("Ongoing Tasks") there; the desktop screen keeps the default "Tasks". It is
// no longer only the EMPTY heading — the tile says the same word whether or not there are tasks — but
// the prop keeps its name because the narrow screen still only overrides the empty case.
export function TasksSummary({
  emptyLabelKey = 'agents.tasksLabel',
  entryRow = false,
  workspaceId,
  workspaceSlug,
  agentId,
  pageName = 'agents',
  onSelect,
}: {
  emptyLabelKey?: string;
  /** Mobile Your Agents: render the compact single-row entry tile with 12px icon-count badges
   *  (Figma 15311:197080), instead of the desktop heading-over-chips summary. */
  entryRow?: boolean;
  /** Scopes the read to one Space (a Space's Dashboard tab). Omit for the account-wide count. */
  workspaceId?: string;
  /** Carried into the link so the Tasks screen opens pre-filtered to that Space. */
  workspaceSlug?: string;
  /** Scopes the counts to ONE agent (its detail screen), and opens `/tasks` pre-filtered to it.
   *  The read stays account-wide — the tasks fetch has no agent axis — and narrows here, exactly as
   *  `TasksBody` narrows its own agent pick client-side. */
  agentId?: string;
  pageName?: string;
  /** Under the map home the tile is a TAB: this runs instead of navigating (the href stays for cmd-click). */
  onSelect?: () => void;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const { state } = useTasks(workspaceId);
  // Captured once at mount — see `useNewSince` for why reading the stamp live would make the badge
  // vanish under the reader the moment they came back from `/tasks`.
  const since = useNewSince('tasks');

  if (state.kind !== 'ready') return null;
  const tasks = agentId ? state.tasks.filter((task) => task.agentId === agentId) : state.tasks;
  const byTab = bucketTasks(tasks);
  const counts = { waiting: byTab.waiting.length, inProgress: byTab.inProgress.length, done: byTab.done.length, failed: byTab.failed.length };
  const total = counts.waiting + counts.inProgress + counts.done + counts.failed;
  const empty = total === 0;

  // Show every category that has anything; Failed never shows at zero (it's the exceptional one).
  const shown = CATS.filter((c) => counts[c.key] > 0);
  // `/api/tasks` stamps `''` for a task it has no date for, which is why this goes through
  // `countNewSince`/`createdAtMs` rather than comparing strings: an undated task is never "new".
  const newCount = since === null ? 0 : countNewSince(tasks, since);

  const href = agentId
    ? `${basePath}/tasks?agent=${encodeURIComponent(agentId)}`
    : workspaceSlug
    ? `${basePath}/tasks?workspace=${encodeURIComponent(workspaceSlug)}`
    : `${basePath}/tasks`;
  const openTasks = (e?: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; button?: number }) => {
    // A modifier click keeps the link's own behaviour (a new tab); a plain click is the tab.
    if (onSelect && !(e?.metaKey || e?.ctrlKey || e?.shiftKey || (e?.button ?? 0) !== 0)) {
      e?.preventDefault();
      onSelect();
    }
    return logPageClickBi({ page_name: pageName, button_name: 'open_tasks' });
  };

  // Mobile entry row (Figma 15311:197080): the title, then one 12px icon-count badge per non-empty
  // status on its own status-chip colour, then the drill-in chevron. Mirrors native's `entryRowCard`.
  if (entryRow) {
    return (
      <AgentEntryRow
        title={empty ? t(emptyLabelKey) : t('agents.tasksLabel')}
        href={href}
        onClick={openTasks}
        testId="agentsTasksSummary"
        trailing={
          <IconCountBadges>
            {shown.map((c) => (
              <IconCountBadge
                key={c.key}
                icon={c.icon}
                count={counts[c.key]}
                variant={c.variant}
                label={t(c.labelKey, { count: counts[c.key] })}
              />
            ))}
          </IconCountBadges>
        }
      />
    );
  }

  return (
    <Card
      component="a"
      interactive
      size="sm"
      className={styles.summaryCard}
      {...({ href } as Record<string, string>)}
      onClick={openTasks}
    >
      <div className={styles.summaryBody}>
        <span className={styles.summaryTitle}>
          <CircleCheckBig size={16} aria-hidden />
          <Typography variant="h4" color="primary" component="span" noWrap className={styles.summaryTitleText}>
            {t(emptyLabelKey)}
          </Typography>
          {newCount > 0 && (
            <Badge variant="agent-profession" className={styles.summaryNew} label={t('summary.newBadge', { count: newCount })} />
          )}
        </span>
        {empty ? (
          <Typography variant="p2" color="secondary">
            {t('agents.taskChip.none')}
          </Typography>
        ) : (
          <div className={styles.summaryChips}>
            {shown.map((c) => (
              <IconCountBadge
                key={c.key}
                icon={c.icon}
                count={counts[c.key]}
                variant={c.variant}
                label={t(c.labelKey, { count: counts[c.key] })}
              />
            ))}
          </div>
        )}
      </div>
      <ChevronRight size={16} className={`${styles.summaryChevron} dir-flip`} aria-hidden />
    </Card>
  );
}
